/* ==========================================================================
   SKNBODY HOMEPAGE - BLOCK 06
   File: assets/skn-home-join.js

   Responsibilities:
   - Preserve Shopify's real newsletter submission
   - Validate the email field
   - Display a loading state during submission
   - Clear old error messages while the user types
   - Focus the success state after Shopify confirms signup
   - Support Shopify Theme Editor section reloads

   This script intentionally does not create a fake client-side success state.
   Shopify controls whether the subscription succeeded.
   ========================================================================== */

(function () {
  'use strict';

  const ROOT_SELECTOR =
    '[data-skn-home-join]';

  if (window.SKNHomeJoin) {
    window.SKNHomeJoin.initAll(document);
    return;
  }

  const sectionStates = new WeakMap();


  /* ==========================================================================
     INITIALIZE SECTION
     ========================================================================== */

  function initSection(root) {
    if (!root || sectionStates.has(root)) {
      return;
    }

    const form = root.querySelector(
      '.skn-home-join__form'
    );

    const input = root.querySelector(
      '[data-skn-join-email]'
    );

    const submitButton = root.querySelector(
      '[data-skn-join-submit]'
    );

    const buttonText = root.querySelector(
      '[data-skn-join-button-text]'
    );

    const errorMessage = root.querySelector(
      '[data-skn-join-error]'
    );

    const successState = root.querySelector(
      '[data-skn-join-success]'
    );

    if (!form) return;

    let isSubmitting = false;


    /* ------------------------------------------------------------------------
       SUCCESS STATE
       ------------------------------------------------------------------------ */

    if (successState) {
      window.setTimeout(function () {
        try {
          successState.focus({
            preventScroll: true
          });
        } catch (error) {
          successState.focus();
        }
      }, 80);
    }


    /* ------------------------------------------------------------------------
       SUBMITTING STATE
       ------------------------------------------------------------------------ */

    function setSubmitting(submitting) {
      isSubmitting = submitting;

      form.classList.toggle(
        'is-submitting',
        submitting
      );

      if (submitButton) {
        submitButton.disabled = submitting;

        submitButton.setAttribute(
          'aria-busy',
          submitting ? 'true' : 'false'
        );
      }

      if (buttonText && submitButton) {
        buttonText.textContent = submitting
          ? 'JOINING'
          : submitButton.dataset.defaultLabel ||
            'JOIN SKNBODY';
      }
    }


    /* ------------------------------------------------------------------------
       FORM VALIDATION
       ------------------------------------------------------------------------ */

    function handleSubmit(event) {
      if (
        !input ||
        !submitButton
      ) {
        return;
      }

      if (isSubmitting) {
        event.preventDefault();
        return;
      }

      input.setCustomValidity('');

      if (!input.value.trim()) {
        event.preventDefault();

        input.setCustomValidity(
          'Please enter your email address.'
        );

        input.reportValidity();
        input.focus();

        return;
      }

      if (!input.validity.valid) {
        event.preventDefault();

        input.reportValidity();
        input.focus();

        return;
      }

      /*
        Do not prevent the valid submission.

        Shopify must process the customer form and decide whether
        the newsletter signup succeeded. The page then returns with
        form.posted_successfully? or form.errors.
      */

      setSubmitting(true);
    }


    /* ------------------------------------------------------------------------
       INPUT CHANGES
       ------------------------------------------------------------------------ */

    function handleInput() {
      if (!input) return;

      input.setCustomValidity('');

      if (errorMessage) {
        errorMessage.hidden = true;
      }
    }


    /* ------------------------------------------------------------------------
       PAGE RESTORE
       ------------------------------------------------------------------------ */

    function handlePageShow() {
      setSubmitting(false);
    }


    /* ------------------------------------------------------------------------
       EVENTS
       ------------------------------------------------------------------------ */

    if (
      submitButton &&
      buttonText
    ) {
      submitButton.dataset.defaultLabel =
        buttonText.textContent.trim();
    }

    if (input && submitButton) {
      form.addEventListener(
        'submit',
        handleSubmit
      );

      input.addEventListener(
        'input',
        handleInput
      );
    }

    window.addEventListener(
      'pageshow',
      handlePageShow
    );


    /* ------------------------------------------------------------------------
       CLEANUP
       ------------------------------------------------------------------------ */

    function destroy() {
      if (input && submitButton) {
        form.removeEventListener(
          'submit',
          handleSubmit
        );

        input.removeEventListener(
          'input',
          handleInput
        );
      }

      window.removeEventListener(
        'pageshow',
        handlePageShow
      );

      sectionStates.delete(root);
    }

    sectionStates.set(root, {
      destroy: destroy
    });
  }


  /* ==========================================================================
     INITIALIZE ALL
     ========================================================================== */

  function initAll(context) {
    const scope = context || document;

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initSection(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  function destroyAll(context) {
    const scope = context || document;
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(function (root) {
        roots.push(root);
      });

    roots.forEach(function (root) {
      const state =
        sectionStates.get(root);

      if (
        state &&
        typeof state.destroy === 'function'
      ) {
        state.destroy();
      }
    });
  }


  /* ==========================================================================
     PUBLIC API
     ========================================================================== */

  window.SKNHomeJoin = {
    initAll: initAll,
    destroyAll: destroyAll
  };


  /* ==========================================================================
     FIRST PAGE LOAD
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      },
      {
        once: true
      }
    );
  } else {
    initAll(document);
  }


  /* ==========================================================================
     SHOPIFY THEME EDITOR
     ========================================================================== */

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      destroyAll(event.target);
    }
  );
})();